import cx from 'classnames'
import dynamic from 'next/dynamic'
import { useContext } from 'react'

import {
  SanityHero,
  SanityHeroBackgroundType,
  SanityHeroContentPosition,
} from '@data/sanity/queries/types/modules'
import { getRatio } from '@lib/image'
import { LayoutContext } from '@lib/layout-context'

import Icon from '@components/icon'
import Photo from '@components/photo'
import ComplexPortableText from '@components/portable-text/complex'

const MuxVideo = dynamic(() => import('@components/video/mux-video'))

type HeroContentProps = Pick<
  SanityHero,
  'bgType' | 'content' | 'contentPosition'
>

type HeroProps = SanityHero

export const HeroContent = ({
  bgType,
  content,
  contentPosition,
}: HeroContentProps) => {
  const center = contentPosition === SanityHeroContentPosition.CENTER
  const bottomLeft = contentPosition === SanityHeroContentPosition.BOTTOM_LEFT

  return (
    <div
      className={cx('container absolute inset-0 z-10 flex flex-col', {
        'justify-end': bottomLeft,
        'justify-center items-center': center,
      })}
    >
      {content && (
        <div
          className={cx('z-10 w-full text-pageBG mt-14', {
            'md:max-w-[70%] xl:max-w-[50%] mb-4 md:mb-10': bottomLeft,
            'lg:max-w-[55%]': center,
          })}
        >
          <ComplexPortableText
            content={content}
            className={cx('rc', {
              'rc-hero-without-bg': bgType === SanityHeroBackgroundType.NONE,
              'rc-hero-with-bg': bgType !== SanityHeroBackgroundType.NONE,
              'text-center': center,
            })}
          />
        </div>
      )}
    </div>
  )
}

const Hero = ({
  content,
  contentPosition,
  showScrollIndicator,
  bgType,
  photos,
  muxVideo,
  videoAspectRatio,
}: HeroProps) => {
  const { isHeaderTransparent } = useContext(LayoutContext)

  return (
    <section
      className={cx('relative overflow-hidden bg-pageBG', {
        'before:absolute before:inset-0 before:z-[1] before:bg-pageText before:bg-opacity-50':
          bgType !== SanityHeroBackgroundType.NONE,
      })}
      style={{
        height: isHeaderTransparent
          ? '100vh'
          : `calc(100vh - var(--headerHeight, var(--defaultHeaderHeight, 0px)))`,
        maxHeight: isHeaderTransparent
          ? '100svh'
          : `calc(100svh - var(--headerHeight, var(--defaultHeaderHeight, 0px)))`,
      }}
    >
      <HeroContent
        bgType={bgType}
        content={content}
        contentPosition={contentPosition}
      />

      <div className="w-full h-full">
        {bgType === SanityHeroBackgroundType.MUX_VIDEO && !!muxVideo && (
          <MuxVideo
            video={muxVideo}
            showControls={false}
            autoplay={true}
            autoplayOnHover={false}
            openModalOnClick={false}
            muted={true}
            loop={true}
            aspectRatio={videoAspectRatio}
            className="h-full"
          />
        )}

        {bgType === SanityHeroBackgroundType.PHOTO && !!photos && (
          <>
            {!!photos.desktopPhoto && (
              <>
                <Photo
                  image={photos.desktopPhoto}
                  fill
                  priority
                  className="relative hidden sm:block w-full h-full z-0"
                  style={{
                    paddingTop: `${
                      100 / getRatio(photos.desktopPhoto.customRatio, 16 / 9)
                    }%`,
                  }}
                  imageClassName="object-cover"
                />
              </>
            )}
            {!!photos.mobilePhoto && (
              <Photo
                image={photos.mobilePhoto}
                fill
                priority
                className="relative sm:hidden w-full h-full z-0"
                style={{
                  paddingTop: `${
                    100 / getRatio(photos.mobilePhoto.customRatio, 5 / 7)
                  }%`,
                }}
                imageClassName="object-cover"
              />
            )}
          </>
        )}

        {showScrollIndicator && (
          <Icon
            name="ScrollIndicator"
            className={cx(
              'absolute bottom-8 left-1/2 text-[40px] text-black z-10',
              'transition-opacity duration-200 hover:opacity-70',
              'animate-pulse',
            )}
          />
        )}
      </div>
    </section>
  )
}

export default Hero
